import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Check, Loader2 } from 'lucide-react';
import api from '@/lib/api';

interface Item {
  id: string;
  line_num: number;
  description: string;
  quantity: number | null;
  unit_value: number | null;
  recommended_hs_code: string | null;
  candidate_codes: any[];
  accepted_hs_code: string | null;
  applied_to_product_id: string | null;
  applied_to_variant_id: string | null;
}

interface Invoice {
  id: string;
  etax_status: string;
  destination_country: string;
  origin_country: string | null;
  total_estimated_duty: number | null;
}

export default function AdminCustomsDetail() {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [applyTarget, setApplyTarget] = useState<Record<string, { kind: 'product' | 'variant'; id: string }>>({});

  useEffect(() => {
    if (!id) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function load() {
      try {
        const data = await api.get<{ invoice: Invoice; items: Item[] }>(`/admin/customs/invoices/${id}`);
        if (!alive) return;
        setInvoice(data.invoice);
        setItems(data.items);
        if (data.invoice.etax_status === 'pending' || data.invoice.etax_status === 'processing') {
          timer = setTimeout(load, 3000);
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : String(err));
      }
    }
    load();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [id]);

  async function accept(item: Item, hs?: string) {
    await api.post(`/admin/customs/invoices/${id}/items/${item.id}/accept`, hs ? { hs_code: hs } : {});
    setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, accepted_hs_code: hs ?? item.recommended_hs_code } : it));
  }

  async function applyToProduct(item: Item) {
    const target = applyTarget[item.id];
    if (!target?.id) return;
    await api.post(`/admin/customs/invoices/${id}/items/${item.id}/apply-to-product`,
      target.kind === 'product' ? { product_id: target.id } : { variant_id: target.id }
    );
    setItems((prev) => prev.map((it) =>
      it.id === item.id
        ? { ...it, applied_to_product_id: target.kind === 'product' ? target.id : null,
            applied_to_variant_id: target.kind === 'variant' ? target.id : null }
        : it
    ));
  }

  if (error) {
    return (
      <div className="p-6">
        <Link to="/admin/customs" className="text-blue-700 inline-flex items-center mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Link>
        <div className="p-3 bg-red-50 text-red-800 rounded">{error}</div>
      </div>
    );
  }

  if (!invoice) {
    return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="p-6 max-w-4xl">
      <Link to="/admin/customs" className="text-blue-700 inline-flex items-center mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Link>
      <h1 className="text-xl font-semibold mb-2">Invoice {invoice.id.slice(0, 8)}</h1>
      <div className="text-sm text-gray-600 mb-4 flex gap-4">
        <span>Status: <strong>{invoice.etax_status}</strong></span>
        <span>Destination: {invoice.destination_country}</span>
        {invoice.origin_country && <span>Origin: {invoice.origin_country}</span>}
        {invoice.total_estimated_duty != null && (
          <span>Total est. duty: <strong>${Number(invoice.total_estimated_duty).toFixed(2)}</strong></span>
        )}
      </div>

      {(invoice.etax_status === 'pending' || invoice.etax_status === 'processing') && (
        <div className="p-4 bg-blue-50 text-blue-900 rounded mb-4 flex items-center">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Waiting for ETaxEngine to finish classifying...
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => {
          const open = expanded.has(item.id);
          return (
            <div key={item.id} className="border rounded">
              <button
                onClick={() => setExpanded((p) => { const n = new Set(p); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; })}
                className="w-full text-left p-3 flex items-start gap-2 hover:bg-gray-50"
              >
                {open ? <ChevronDown className="w-4 h-4 mt-1" /> : <ChevronRight className="w-4 h-4 mt-1" />}
                <div className="flex-1">
                  <div className="font-medium">Line {item.line_num}: {item.description}</div>
                  <div className="text-sm text-gray-600">
                    Recommended: <code className="bg-gray-100 px-1">{item.recommended_hs_code || '—'}</code>
                  </div>
                </div>
                {item.accepted_hs_code && (
                  <span className="text-sm text-emerald-700 inline-flex items-center"><Check className="w-4 h-4 mr-1" />{item.accepted_hs_code}</span>
                )}
              </button>
              {open && (
                <div className="border-t p-3 space-y-3">
                  {(item.candidate_codes || []).map((c: any, idx: number) => (
                    <div key={idx} className="p-2 rounded border border-gray-200 text-sm">
                      <code className="font-mono">{c.hs_code}</code> — {c.description}
                      <span className="ml-3 text-gray-500">Conf {(Number(c.confidence) * 100).toFixed(0)}% · Duty ${Number(c.estimated_duty || 0).toFixed(2)}</span>
                      <p className="mt-1 text-gray-700">{c.reasoning}</p>
                      {!item.accepted_hs_code && (
                        <button
                          onClick={() => accept(item, c.hs_code)}
                          className="mt-1 text-xs px-2 py-1 rounded bg-emerald-600 text-white"
                        >Accept</button>
                      )}
                    </div>
                  ))}
                  {item.accepted_hs_code && !item.applied_to_product_id && !item.applied_to_variant_id && (
                    <div className="pt-2 border-t flex items-center gap-2 text-sm">
                      <select
                        value={applyTarget[item.id]?.kind || 'product'}
                        onChange={(e) => setApplyTarget((p) => ({ ...p, [item.id]: { kind: e.target.value as 'product' | 'variant', id: p[item.id]?.id || '' } }))}
                        className="rounded border-gray-300 text-sm"
                      >
                        <option value="product">Product</option>
                        <option value="variant">Variant</option>
                      </select>
                      <input
                        placeholder={`${applyTarget[item.id]?.kind || 'product'} ID`}
                        value={applyTarget[item.id]?.id || ''}
                        onChange={(e) => setApplyTarget((p) => ({ ...p, [item.id]: { kind: p[item.id]?.kind || 'product', id: e.target.value } }))}
                        className="flex-1 rounded border-gray-300 text-sm"
                      />
                      <button
                        onClick={() => applyToProduct(item)}
                        disabled={!applyTarget[item.id]?.id}
                        className="px-3 py-1 rounded bg-gray-700 text-white text-sm disabled:bg-gray-400"
                      >Apply</button>
                    </div>
                  )}
                  {(item.applied_to_product_id || item.applied_to_variant_id) && (
                    <div className="pt-2 border-t text-sm text-gray-600">
                      Applied to {item.applied_to_product_id ? 'product' : 'variant'} {(item.applied_to_product_id || item.applied_to_variant_id)?.slice(0, 8)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
